
import React, { useState, useEffect } from 'react';
import apiClient from '../../services/api';
import { Trash2, Plus } from 'lucide-react';
import { showToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface ShiftPatternTemplate {
    id: string;
    name: string;
    daysOn: number;
    daysOff: number;
    description?: string;
}

const ShiftPatternManager = () => {
    const [patterns, setPatterns] = useState<ShiftPatternTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    // New pattern form state
    const [newName, setNewName] = useState('');
    const [newDaysOn, setNewDaysOn] = useState(4);
    const [newDaysOff, setNewDaysOff] = useState(4);

    useEffect(() => {
        fetchPatterns();
    }, []);

    const fetchPatterns = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/patterns');
            setPatterns(res.data);
        } catch (err) {
            setError('Failed to load patterns');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await apiClient.post('/patterns', {
                name: newName,
                daysOn: newDaysOn,
                daysOff: newDaysOff
            });
            setNewName('');
            fetchPatterns();
        } catch (err) {
            setError('Failed to create pattern');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await apiClient.delete(`/patterns/${id}`);
            setPendingDeleteId(null);
            showToast('Pattern deleted', 'success');
            fetchPatterns();
        } catch (err) {
            setError('Failed to delete pattern');
        }
    };

    return (
        <>
        <div className="p-6 bg-white rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">Shift Patterns</h2>

            {error && <div className="p-3 bg-red-100 text-red-700 rounded mb-4">{error}</div>}

            <div className="mb-6 border-b pb-4">
                <h3 className="font-semibold mb-2">Create New Pattern</h3>
                <form onSubmit={handleCreate} className="flex gap-4 items-end">
                    <div>
                        <label className="block text-sm text-gray-600">Name</label>
                        <input
                            className="border p-2 rounded"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            placeholder="e.g. 5 On 3 Off"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600">Days On</label>
                        <input
                            type="number"
                            className="border p-2 rounded w-20"
                            value={newDaysOn}
                            onChange={e => setNewDaysOn(Number(e.target.value))}
                            min={1}
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600">Days Off</label>
                        <input
                            type="number"
                            className="border p-2 rounded w-20"
                            value={newDaysOff}
                            onChange={e => setNewDaysOff(Number(e.target.value))}
                            min={1}
                        />
                    </div>
                    <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700">
                        <Plus size={16} /> Add Pattern
                    </button>
                </form>
            </div>

            {loading ? <p>Loading...</p> : (
                <table className="w-full text-left">
                    <thead>
                        <tr className="border-b bg-gray-50">
                            <th className="p-3">Name</th>
                            <th className="p-3">Structure</th>
                            <th className="p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {patterns.map(p => (
                            <tr key={p.id} className="border-b">
                                <td className="p-3 font-medium">{p.name}</td>
                                <td className="p-3">{p.daysOn} Days On / {p.daysOff} Off</td>
                                <td className="p-3">
                                    <button onClick={() => setPendingDeleteId(p.id)} className="text-red-500 hover:text-red-700">
                                        <Trash2 size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {patterns.length === 0 && (
                            <tr><td colSpan={3} className="p-4 text-center text-gray-500">No custom patterns defined.</td></tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>

            <ConfirmDialog
                open={!!pendingDeleteId}
                title="Delete Pattern"
                message="Are you sure you want to delete this shift pattern?"
                confirmLabel="Delete"
                variant="danger"
                onConfirm={() => pendingDeleteId && handleDelete(pendingDeleteId)}
                onCancel={() => setPendingDeleteId(null)}
            />
        </>
    );
};

export default ShiftPatternManager;
